import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car } from 'lucide-react';
import { useLocalizedNavigation } from '@/hooks/useLocalizedNavigation';
import SocialIcons from '@/components/SocialIcons';
import TrustpilotWidget from '@/components/TrustpilotWidget';

// أيقونات وسائل الدفع - تصميم أنيق
const PaymentIcons = () => (
  <div className="flex items-center justify-center gap-2">
    {/* Visa */}
    <div className="bg-white rounded-md px-3 py-1.5 flex items-center justify-center min-w-[50px]">
      <span className="text-[#1A1F71] font-bold text-sm italic tracking-tight">VISA</span>
    </div>
    
    {/* Mastercard */}
    <div className="bg-slate-800 rounded-md px-2 py-1.5 flex items-center justify-center">
      <svg className="h-5" viewBox="0 0 131.39 86.9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="43.45" cy="43.45" r="43.45" fill="#EB001B"/>
        <circle cx="87.94" cy="43.45" r="43.45" fill="#F79E1B"/>
        <path d="M65.7 11.5C56.05 19.15 50 30.6 50 43.45C50 56.3 56.05 67.75 65.7 75.4C75.35 67.75 81.4 56.3 81.4 43.45C81.4 30.6 75.35 19.15 65.7 11.5Z" fill="#FF5F00"/>
      </svg>
    </div>
    
    {/* Klarna */}
    <div className="bg-[#FFB3C7] rounded-md px-2.5 py-1.5 flex items-center justify-center">
      <span className="text-black font-bold text-sm tracking-tight">Klarna.</span>
    </div>
  </div>
);

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const { getLink } = useLocalizedNavigation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'fa';

  return (
    <footer className="bg-slate-900 text-white border-t border-slate-800 mt-auto" dir={isRTL ? 'rtl' : 'ltr'} data-testid="footer">
      {/* Social row — auto-hides itself when admin has not configured any URLs. */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-300">
            {{
              sv: 'Följ oss',
              ar: 'تابعنا',
              en: 'Follow us',
              tr: 'Bizi takip et',
              fa: 'ما را دنبال کنید',
              so: 'Na raac',
              pl: 'Obserwuj nas',
              es: 'Síguenos',
              de: 'Folge uns'
            }[i18n.language] || 'Follow us'}
          </span>
          <SocialIcons size="md" />
        </div>
      </div>

      {/* Trustpilot Review Collector — invites customers to leave a review */}
      <div className="border-b border-slate-800 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-5">
          <TrustpilotWidget theme="dark" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          
          {/* Brand + Copyright */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Car className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold">DriveMaster</span>
            <span className="text-xs text-slate-500">©</span>
          </div>

          {/* Payment Methods - في الوسط */}
          <PaymentIcons />

          {/* Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs text-slate-400">
            <Link to={getLink('/integritet')} className="hover:text-emerald-400 transition-colors">{t('footer.privacy')}</Link>
            <span className="text-slate-700">|</span>
            <Link to={getLink('/villkor')} className="hover:text-emerald-400 transition-colors">{t('footer.terms')}</Link>
            <span className="text-slate-700">|</span>
            <Link to="/aterbetalning" className="hover:text-emerald-400 transition-colors">{t('footer.refund')}</Link>
            <span className="text-slate-700">|</span>
            <Link to="/ansvarsfriskrivning" className="hover:text-emerald-400 transition-colors">{t('footer.disclaimer')}</Link>
            <span className="text-slate-700">|</span>
            <Link to="/cookies" className="hover:text-emerald-400 transition-colors">{t('footer.cookies')}</Link>
            <span className="text-slate-700">|</span>
            <Link to="/recensioner" className="hover:text-emerald-400 transition-colors">{({sv:'Recensioner',en:'Reviews',ar:'التقييمات',tr:'Yorumlar',fa:'نظرات',so:'Dib-u-eegis',pl:'Recenzje',es:'Reseñas',de:'Bewertungen'})[i18n.language] || 'Reviews'}</Link>
            <span className="text-slate-700">|</span>
            {/* B-körkort + Taxikort links — pair the two license shortcuts so
                users (and Google crawlers) see them together in the footer. */}
            <Link to="/korkortsteori" className="hover:text-blue-400 transition-colors font-semibold">B-körkort</Link>
            <span className="text-slate-700">|</span>
            <Link to="/taxikort" className="hover:text-amber-400 transition-colors font-semibold">Taxikort</Link>
            <span className="text-slate-700">|</span>
            <Link to={getLink('/support')} className="hover:text-emerald-400 transition-colors">{t('support.title')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
